import { staleTimes } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import type { OptionIndex } from '../catalog/options.ts';
import {
  productIdsOf,
  singleVariantOf,
  useCodeLookup,
  variantIdsOf,
  type VariantMatch,
} from '../catalog/useCodeLookup.ts';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import { sellsFor } from '../companies.ts';
import { saleableVariantsOf, type SaleableVariant } from './new-order-form.ts';
import { fetchStockLevels } from '../inventory/stock-levels.ts';

export type ScannedCodes = {
  readonly productIds: readonly string[];
  readonly variantIds: ReadonlySet<string> | null;
};

export type ScanNotice = { readonly tone: 'success' | 'warning'; readonly message: string };

export type VariantScanInput = {
  readonly api: TypedClient;
  readonly locationId: string;
  readonly company: string;
  readonly index: OptionIndex;
  readonly addedIds: ReadonlySet<string>;
  readonly onAdd: (variant: SaleableVariant) => void;
};

export const useVariantScan = ({ api, locationId, company, index, addedIds, onAdd }: VariantScanInput) => {
  const { t, locale, tenantId, queryClient } = usePageContext();
  const lookup = useCodeLookup(api);
  const [scanned, setScanned] = useState<ScannedCodes | null>(null);
  const [notice, setNotice] = useState<ScanNotice | null>(null);
  const [scans, setScans] = useState(0);

  const productOf = (id: string) =>
    queryClient.query({
      queryKey: queryKeys.product(tenantId, id),
      queryFn: () => api.get('/admin/products/{id}', { path: { id } }),
      staleTime: staleTimes.catalog,
    });

  const availableOf = async (variantId: string): Promise<number> => {
    const levels = await queryClient.query({
      queryKey: queryKeys.stockLevels(tenantId, { variantIds: [variantId] }),
      queryFn: () => fetchStockLevels(api, [variantId]),
      staleTime: staleTimes.inventory,
    });
    return levels.items.find((level) => level.locationId === locationId)?.available ?? 0;
  };

  const addScanned = async (match: VariantMatch) => {
    const product = await productOf(match.productId);
    const variant = saleableVariantsOf([product], index, locale).find(
      (candidate) => candidate.variantId === match.variantId,
    );
    if (variant === undefined || product.status === 'archived') {
      setNotice({ tone: 'warning', message: t('newOrder.scanArchived') });
      return;
    }
    if (!sellsFor(product.subsidiaryId, company)) {
      setNotice({ tone: 'warning', message: t('newOrder.scanOtherCompany', { name: variant.label }) });
      return;
    }
    if (!addedIds.has(variant.variantId) && (await availableOf(variant.variantId)) === 0) {
      setNotice({ tone: 'warning', message: t('newOrder.scanOutOfStock', { name: variant.label }) });
      return;
    }
    onAdd(variant);
    setScans((count) => count + 1);
    setNotice({ tone: 'success', message: t('newOrder.scanAdded', { name: variant.label }) });
  };

  const search = async (text: string) => {
    setNotice(null);
    setScanned(null);
    if (text === '') return;
    const matches = await lookup(text).catch(() => []);
    const single = singleVariantOf(matches);
    if (single !== null) {
      await addScanned(single);
      return;
    }
    if (matches.length > 0)
      setScanned({ productIds: productIdsOf(matches), variantIds: variantIdsOf(matches) });
  };

  return {
    scanned,
    notice,
    scans,
    search: (text: string) => {
      void search(text);
    },
    clear: () => {
      setScanned(null);
      setNotice(null);
    },
  };
};
